import * as React from 'react';
import classnames from 'classnames';
import './VideoAndAttachments.css';
import ToggleGroup from '../../../../../../../components/button/ToggleGroup';
import { useNamespacedTranslation } from '../../../../../../../hooks/useNamespacedTranslation';
import { useState } from 'react';
import {
  DocumentPreview,
  isPreviewableType,
} from '../../../../Documents2/DocumentInfo/DocumentPreview';
// Imported from the module rather than the Documents2 barrel: the barrel pulls
// in DocumentsPage -> routes -> Workflows, which would be an import cycle.
import { DownloadLink } from '../../../../Documents2/DownloadLink';
import { WorkflowItem } from '../../../WorkflowItems/types';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import { VideoPlayer } from '../../../../../../../components/VideoPlayer';
import { Maybe } from '../../../../../../../utils/maybe';
import Button from '../../../../../../../components/button';

type Props = {
  workflowItem: WorkflowItem | null;
  onPlayVideo: () => void;
};

const VIEWS = Object.freeze({
  ATTACHMENT: 'ATTACHMENT',
  VIDEO: 'VIDEO',
});

type Attachment = WorkflowItem['attachments'][number] & {
  viewLink: string;
  fileType: string;
};

const VideoAndAttachments: React.FC<React.PropsWithChildren<Props>> = ({
  workflowItem,
  onPlayVideo,
}) => {
  const t = useNamespacedTranslation(
    'performanceReview.workflowsItems.sidebar',
  );

  const [currentView, setCurrentView] = useState<Maybe<string>>(
    workflowItem && workflowItem.attachments.length > 0
      ? VIEWS.ATTACHMENT
      : VIEWS.VIDEO,
  );
  const isAttachmentView = currentView === VIEWS.ATTACHMENT;

  const [selectedFileToPreview, setSelectedFileToPreview] =
    useState<Maybe<Attachment>>(null);

  if (!workflowItem) {
    return <></>;
  }

  const hasAttachments = workflowItem.attachments.length > 0;
  const hasVideo =
    workflowItem.instructionsLoomUrl !== '' &&
    workflowItem.instructionsLoomUrl != null;

  if (!hasAttachments && !hasVideo) {
    return <></>;
  }

  const attachments = workflowItem.attachments.filter(
    (attachment) => attachment.viewLink && attachment.fileType,
  ) as Attachment[];

  const onlyPreviewableAttachment =
    attachments.length === 1 && isPreviewableType(attachments[0].fileType)
      ? attachments[0]
      : null;
  const fileToPreview = selectedFileToPreview ?? onlyPreviewableAttachment;

  const renderAttachments = () => {
    if (fileToPreview) {
      return (
        <>
          {attachments.length > 1 && (
            <Button
              variant="outline-dark"
              className="rounded-circle mb-3 back-btn"
              size="sm"
              icon="arrow-back-outline"
              onClick={() => setSelectedFileToPreview(null)}
            />
          )}
          <DocumentPreview
            url={fileToPreview.viewLink}
            type={fileToPreview.fileType}
            width="100%"
          />
        </>
      );
    }

    return (
      <ul className="list-unstyled mb-0">
        {attachments.map((attachment) => (
          <li key={attachment.documentId} className="mb-3">
            {isPreviewableType(attachment.fileType) ? (
              <button
                type="button"
                className="btn btn-link p-0 text-decoration-none align-baseline"
                onClick={() => setSelectedFileToPreview(attachment)}>
                <FlairIcon icon="document-outline" className="me-3" />
                {attachment.fileName}
              </button>
            ) : (
              <DownloadLink file={attachment} />
            )}
          </li>
        ))}
      </ul>
    );
  };

  const renderVideoPreview = () => {
    if (hasVideo && workflowItem.instructionsLoomUrl) {
      return (
        <VideoPlayer
          url={workflowItem.instructionsLoomUrl}
          className="rounded"
          onPlay={onPlayVideo}
        />
      );
    }
  };

  const getToggleBtns = () => {
    let buttons = [];
    if (hasAttachments) {
      const hasMultipleAttachments = workflowItem.attachments.length > 1;
      buttons.push({
        name: VIEWS.ATTACHMENT,
        title: t(hasMultipleAttachments ? 'attachments' : 'attachment'),
      });
    }

    if (hasVideo) {
      buttons.push({ name: VIEWS.VIDEO, title: t('video') });
    }

    return buttons;
  };

  const hasMultipleAttachment =
    hasAttachments && workflowItem.attachments.length > 1;

  const shouldRenderToggles =
    hasMultipleAttachment || (hasAttachments && hasVideo);

  // Only an embedded preview or the video player fills the pane. A plain list of
  // file links does not, so reserving the full preview area for it would leave a
  // large empty gap and needlessly squeeze the task details next to it.
  const fillsPane = isAttachmentView ? !!fileToPreview : hasVideo;

  return (
    <div
      className={classnames(
        'mx-3',
        fillsPane ? 'video-attachment-container w-75' : 'attachment-links',
      )}>
      {shouldRenderToggles && (
        <ToggleGroup
          buttons={getToggleBtns()}
          defaultValue={getToggleBtns()[0].name}
          variant="secondary-2"
          containerClassName="w-100"
          onChange={setCurrentView}
        />
      )}
      <div className={classnames('mt-4', fillsPane && 'h-100')}>
        {isAttachmentView ? renderAttachments() : renderVideoPreview()}
      </div>
    </div>
  );
};

export default VideoAndAttachments;
